import { useCallback } from "react"
import { AutoExpandCollapse } from "components/ui/collapse/AutoExpandCollapse/AutoExpandCollapse"
import {
	flattenModelNumberToRows,
	isNestedCategory,
	isModelNumberArray,
} from "components/pages/PageConfiguration/layout/ModelNumbers/layout/TableModelNumbers/TableModelNumbers.helper"
import { ModelNumbersCategoryTable } from "./layout/ModelNumbersCategoryTable/ModelNumbersCategoryTable"
import type { TModelNumbersData, TModelNumberTableRow, TModelNumber } from "types/modelNumbers.type"
import styles from "./TableModelNumbers.module.scss"

const { collapseLabel, collapseBadge, customBorderedTable } = styles

// Count total items in a subcategory
const countItemsInSubCategory = (modelNumbers: TModelNumber[]): number => {
	return modelNumbers.length
}

// Count total items in all subcategories
const countItemsInNestedCategory = (subCategory: { [key: string]: TModelNumber[] }): number => {
	return Object.values(subCategory).reduce((total, modelNumbers) => {
		return total + countItemsInSubCategory(modelNumbers)
	}, 0)
}

// Count total items in the entire nested structure
const countItemsInTopLevel = (nestedCategory: { [key: string]: { [key: string]: TModelNumber[] } }): number => {
	return Object.values(nestedCategory).reduce((total, subCategory) => {
		return total + countItemsInNestedCategory(subCategory)
	}, 0)
}

type Props = {
	onRowHandle: (record: TModelNumberTableRow) => { onClick: () => void }
	/**
	 * Opens the inner levels while a search is active. The data is already filtered to
	 * matching model numbers, and the tables live two levels down, so without this a
	 * search in a nested category expands only the top level and shows nothing.
	 */
	isSearching?: boolean
	searchTerm?: string
}

export const useCollapseModelNumbers = ({ onRowHandle, isSearching = false, searchTerm = "" }: Props) => {
	// Generate third level collapse items (subcategories with table)
	const generateThirdLevelCollapseItems = useCallback(
		(subCategory: { [key: string]: TModelNumber[] }) => {
			return Object.entries(subCategory).map(([subCategoryName, modelNumbers]) => {
				const tableData = modelNumbers ? modelNumbers.flatMap(flattenModelNumberToRows) : []
				const count = tableData.length

				return {
					key: subCategoryName,
					label: (
						<span className={collapseLabel}>
							<span>{subCategoryName}</span>
							<span className={collapseBadge}>{count}</span>
						</span>
					),
					"data-testid": "collapse-third-level",
					children: (
						<ModelNumbersCategoryTable
							data={tableData}
							onRow={onRowHandle}
							className={customBorderedTable}
							scrollY={400}
						/>
					),
				}
			})
		},
		[onRowHandle],
	)

	// Generate second level collapse items (nested categories)
	const generateSecondLevelCollapseItems = useCallback(
		(nestedCategory: { [key: string]: { [key: string]: TModelNumber[] } }, topLevelKey: string) => {
			return Object.entries(nestedCategory).map(([nestedCategoryName, subCategory]) => {
				const count = countItemsInNestedCategory(subCategory)

				return {
					key: `${topLevelKey}-${nestedCategoryName}`,
					label: (
						<span className={collapseLabel}>
							<span>{nestedCategoryName}</span>
							<span className={collapseBadge}>{count}</span>
						</span>
					),
					"data-testid": "collapse-second-level",
					children: (
						<div>
							<AutoExpandCollapse
								items={generateThirdLevelCollapseItems(subCategory)}
								isExpanded={isSearching}
								expandKey={searchTerm}
							/>
						</div>
					),
				}
			})
		},
		[generateThirdLevelCollapseItems, isSearching, searchTerm],
	)

	// Generate top level collapse items
	const generateCollapseItems = useCallback(
		(modelNumbersData: TModelNumbersData) => {
			if (!modelNumbersData) return []

			return Object.entries(modelNumbersData).map(([topLevelCategory, value]) => {
				// Handle "Without category" case (array of model numbers)
				if (topLevelCategory === "Without category" && isModelNumberArray(value)) {
					const tableData = value.flatMap(flattenModelNumberToRows)
					const count = tableData.length

					return {
						key: topLevelCategory,
						label: (
							<span className={collapseLabel}>
								<span>{topLevelCategory}</span>
								<span className={collapseBadge}>{count}</span>
							</span>
						),
						"data-testid": "collapse-top-level",
						// Holds every uncategorised model number — thousands of them — and is what
						// the virtualization is actually for. The nested tables render through the
						// same component so the page has one kind of table rather than two.
						children: (
							<ModelNumbersCategoryTable
								data={tableData}
								onRow={onRowHandle}
								className={customBorderedTable}
								scrollY={600}
							/>
						),
					}
				}

				// Handle nested categories
				if (isNestedCategory(value)) {
					const count = countItemsInTopLevel(value)

					return {
						key: topLevelCategory,
						label: (
							<span className={collapseLabel}>
								<span>{topLevelCategory}</span>
								<span className={collapseBadge}>{count}</span>
							</span>
						),
						"data-testid": "collapse-top-level",
						children: (
							<div>
								<AutoExpandCollapse
									items={generateSecondLevelCollapseItems(value, topLevelCategory)}
									isExpanded={isSearching}
									expandKey={searchTerm}
								/>
							</div>
						),
					}
				}

				// Fallback for unexpected structure
				return {
					key: topLevelCategory,
					label: topLevelCategory,
					"data-testid": "collapse-top-level",
					children: <div>No data available</div>,
				}
			})
		},
		[generateSecondLevelCollapseItems, onRowHandle, isSearching, searchTerm],
	)

	return { generateCollapseItems }
}
